import React, { useState } from 'react';
import Button from '../Common/Button';
import { formatUSDT, formatTimestamp, formatTimeRemaining, percentProgress } from '../../utils/format';
import { PLAN_INFO } from '../../config/contract';
import { useWeb3 } from '../../context/Web3Context';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';

const TYPE_LABEL = ['Real', 'Virtual', 'Leader'];

const InvestmentRow = ({ inv, index, onChange }) => {
  const { contract, onCorrectNetwork, switchToCorrectNetwork } = useWeb3();
  const [busy, setBusy] = useState(false);

  const plan = PLAN_INFO[inv.plan] || PLAN_INFO[0];
  const now = Math.floor(Date.now() / 1000);
  const matured = Number(inv.maturityTime.toString()) <= now;
  const timeLeft = matured ? 0 : Number(inv.maturityTime.toString()) - now;
  const progress = percentProgress(inv.roiEarned, inv.maxROI);
  const investmentType = TYPE_LABEL[inv.investmentType] || 'Real';

  const withdraw = async () => {
    if (!contract) return;
    if (!onCorrectNetwork) {
      notify.warning('Switching to correct network...');
      const ok = await switchToCorrectNetwork();
      if (!ok) return;
      await new Promise((r) => setTimeout(r, 600));
    }
    setBusy(true);
    let lid = null;
    try {
      lid = notify.loading('Withdrawing principal...');
      const tx = await contract.withdrawPrincipal(index);
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success('Principal withdrawn');
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="investment-row">
      <div className="inv-plan-tag" style={{
        background: `${plan.color}15`,
        borderColor: `${plan.color}50`
      }}>
        <div className="inv-plan-days" style={{ color: plan.color, fontSize: 18 }}>{plan.days}</div>
        <div className="inv-plan-unit">Days</div>
        <div style={{
          fontFamily: 'Orbitron, sans-serif',
          fontSize: 8,
          letterSpacing: '0.15em',
          color: plan.color,
          marginTop: 2,
          textShadow: `0 0 6px ${plan.color}80`
        }}>{plan.rank}</div>
      </div>

      <div className="inv-details">
        <div className="inv-amount-row">
          <span className="inv-amount">{formatUSDT(inv.amount)}</span>
          <span className="inv-amount-unit">USDT</span>
          {investmentType !== 'Real' && (
            <span className={`badge badge-${investmentType === 'Virtual' ? 'purple' : 'orange'}`}>
              {investmentType}
            </span>
          )}
          {inv.principalWithdrawn && <span className="badge badge-muted">Withdrawn</span>}
          {!inv.active && !inv.principalWithdrawn && investmentType === 'Real' && (
            <span className="badge badge-green">Completed</span>
          )}
        </div>

        <div className="inv-meta">
          <div>
            <span className="inv-meta-label">Started:</span>
            <span className="inv-meta-value">{formatTimestamp(inv.timestamp)}</span>
          </div>
          <div>
            <span className="inv-meta-label">Earned:</span>
            <span className="inv-meta-value text-green">{formatUSDT(inv.roiEarned)} / {formatUSDT(inv.maxROI)}</span>
          </div>
          <div>
            <span className="inv-meta-label">Status:</span>
            <span className="inv-meta-value" style={{ color: matured ? 'var(--accent-green)' : 'var(--accent-cyan)' }}>
              {matured ? 'Matured' : formatTimeRemaining(timeLeft)}
            </span>
          </div>
        </div>

        <div className="inv-progress">
          <div className="inv-progress-info">
            <span>ROI Progress</span>
            <span>{progress.toFixed(1)}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="inv-actions">
        {investmentType === 'Real' && !inv.principalWithdrawn && (
          <Button
            variant={matured ? 'success' : 'ghost'}
            size="sm"
            onClick={withdraw}
            loading={busy}
          >
            {matured ? 'Withdraw' : 'Early Exit'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default InvestmentRow;
